<template>
    <div class="email-broadcast-page">
        <div v-if="!showEmptyState" class="filter">
            <DsInputField
                id="search-entry"
                v-model="search"
                data-qa="email-broadcast-search"
                :label="$options.MESSAGE_CONSTANTS.SEARCH"
                type="search"
            />

            <div class="filter-actions">
                <DsGuide
                    v-if="isApplePrivacyIssuesEnabled"
                    data-qa="open-rates-guide"
                    has-close
                    position="bottom-end"
                    :body-text="$options.MESSAGE_CONSTANTS.GUIDE_BODY"
                    :confirm-text="$options.MESSAGE_CONSTANTS.GUIDE_LEARN_MORE"
                    :show="showBroadcastsPageApplePrivacyGuide"
                    @confirm="openAppleMailPrivacyInNewTab"
                    @cancel="completeApplePrivacyGuide"
                >
                    <template #reference>
                        <DsTooltip
                            wrap
                            class="opens-tooltip"
                            data-qa="opens-tooltip"
                        >
                            <template #reference>
                                <DsTextButton
                                    data-qa="show-opens-toggle"
                                    @click="toggleShowHideEmailOpens"
                                >
                                    <template #leading>
                                        <DsIcon
                                            :name="showOpensToggleIcon"
                                            data-qa="show-opens-toggle-icon"
                                        />
                                    </template>

                                    <template #default>
                                        {{ showOpensToggleText }}
                                    </template>
                                </DsTextButton>
                            </template>

                            <template #default>
                                <div class="tooltip-body">
                                    {{ $options.MESSAGE_CONSTANTS.OPENS_TOOLTIP }}
                                </div>
                            </template>
                        </DsTooltip>
                    </template>
                </DsGuide>

                <EmailBroadcastFilters
                    data-qa="email-broadcast-filters"
                    :filter.sync="filter"
                />

                <EmailBroadcastMoreActions />
            </div>
        </div>

        <EmptyResults
            :filter="filter.label"
            :no-filter-results="noFilterResults"
            :no-search-results="noSearchResults"
            :search="search"
        />

        <EmailBroadcastItem
            v-for="(broadcast) in broadcasts"
            v-show="!showEmptyState"
            :key="`${broadcast.id}-${broadcast.status}`"
            :data-qa="`${broadcast.id}-${broadcast.status}`"
            :broadcast="broadcast"
            :is-bard-builder-disabled="isBardBuilderDisabled"
            :show-opens="!isApplePrivacyIssuesEnabled || showEmailOpens"
            @cancel="stopBroadcast"
            @resume="resumeBroadcast"
            @delete-draft="(id) => deleteDraft(broadcast.editor, id)"
            @edit-broadcast="editBroadcast(broadcast)"
            @copy-broadcast="copyBroadcast(broadcast)"
            @open-reschedule-modal="openRescheduleModal"
        />

        <BroadcastsEmptyState
            v-if="showEmptyState"
            data-qa="empty-state"
            @new-email="createNewEmailBroadcast"
        />

        <DsInfiniteScroll
            v-if="!showEmptyState"
            :limit.sync="limit"
            :loading="loading"
            :no-more-data="noMoreData"
            @load="loadEmailBroadcasts"
        >
            <div class="placeholders">
                <div v-for="i in 3" :key="i" class="placeholder">
                    <DsPlaceholder :rows="placeholder" />
                </div>
            </div>
        </DsInfiniteScroll>

        <RescheduleBroadcastDialog
            v-if="isRescheduleModalOpen"
            :user-locale="userLocale"
            :sub-title="broadcastSubject"
            @close="closeRescheduleModal"
            @reschedule="rescheduleEmailBroadcast"
        />

        <portal to="root">
            <div
                v-if="broadcastCopyInProgress"
                data-qa="spinner-container"
                :class="['spinner-container', { 'fixed': spinnerContainerPositionFixed }]"
            >
                <DsSpinner />
            </div>
        </portal>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import moment from 'moment';
import EmailBroadcastItem from './EmailBroadcastItem';
import BroadcastsEmptyState from './EmailBroadcastEmptyState';
import EmptyResults from '@/components/shared/EmptyResults';
import EmailBroadcastFilters from './EmailBroadcastFilters';
import RescheduleBroadcastDialog from './RescheduleBroadcastDialog.vue';
import EmailBroadcastMoreActions from './EmailBroadcastMoreActions';
import {
    OPEN_RATES_HELP_ARTICLE_URL,
    EMAIL_EDITORS,
    BROADCAST_STATUS,
    PENDING_STATUSES,
    POLLING,
    VALID_DISPLAY_STATUSES,
} from '../constants/emailBroadcast.constants';

import {
    getAllEmailBroadcasts,
    stopEmailBroadcast,
    resumeEmailBroadcast,
    deleteDraft,
    deleteUnlayerDraft,
    copyBroadcast,
    copyUnlayerBroadcast,
    completeApplePrivacyGuideEmailBroadcastPage,
    saveShowHideOpenRatesUserPreference,
    rescheduleBroadcast,
    getUnlayerBroadcastReportByBroadcastId,
} from '@/api/broadcasts/emailBroadcast';

import amplitude from '@/components/util/analytics/amplitude';
import { SEARCHING_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';

export const MESSAGE_CONSTANTS = {
    SEARCH: 'Search email broadcasts',
    ERROR_LOADING: 'There was an error loading your email broadcasts',
    SHOW_OPENS: 'Show opens',
    HIDE_OPENS: 'Hide opens',
    OPENS_TOOLTIP: 'With Apple Mail Privacy Protection, Apple Mail users can prevent their email opens from being tracked. This might affect how accurate your open rates are.',
    GUIDE_BODY: 'Email open rates are less accurate as a result of new industry privacy regulations, so they are hidden by default. If you\'d like to display estimated open rates, you can from here.',
    GUIDE_LEARN_MORE: 'Learn more',
};

export default {
    name: 'EmailBroadcastPage',

    MESSAGE_CONSTANTS,

    getAllEmailBroadcasts,
    stopEmailBroadcast,
    resumeEmailBroadcast,
    deleteDraft,
    copyBroadcast,
    completeApplePrivacyGuideEmailBroadcastPage,
    saveShowHideOpenRatesUserPreference,

    components: {
        EmailBroadcastFilters,
        EmailBroadcastItem,
        EmailBroadcastMoreActions,
        BroadcastsEmptyState,
        EmptyResults,
        RescheduleBroadcastDialog,
    },

    props: {
        userLocale: {
            type: String,
            default: '',
        },
        isApplePrivacyIssuesEnabled: Boolean,
        isBardBuilderDisabled: Boolean,
        lastSentEmailBroadcastId: Number,
        showBroadcastsPageApplePrivacyGuideInitialValue: Boolean,
        showEmailOpensInitialValue: Boolean,
        broadcastReloadTimeout: {
            type: Number,
            default: 1000,
        },
        debounceDelay: {
            type: Number,
            default: SEARCHING_DEBOUNCE_DELAY,
        },
    },

    data() {
        return {
            broadcasts: [],
            limit: 10,
            offset: 0,
            noMoreData: false,
            loading: true,
            broadcastCopyInProgress: false,
            spinnerContainerPositionFixed: false,
            scrollY: 0,
            filter: {},
            firstLoad: true,
            search: '',
            placeholder: [
                { height: '5rem', boxes: [2, 1, 1, 1] },
            ],
            pollTimeout: null,
            showBroadcastsPageApplePrivacyGuide: this.showBroadcastsPageApplePrivacyGuideInitialValue,
            showEmailOpens: this.showEmailOpensInitialValue,
            isRescheduleModalOpen: false,
            broadcastSubject: '',
        };
    },

    created() {
        if (this.debounceDelay > 0) {
            this.updateSearch = debounce(this.updateSearch, this.debounceDelay);
        }
    },

    watch: {
        filter() {
            this.reload();
        },

        search() {
            this.updateSearch();
        },
    },

    computed: {
        activeFilterValue() {
            return this.filter != null && Boolean(this.filter.value)
                ? this.filter.value
                : null;
        },

        isFilterOrSearch() {
            return Boolean(this.activeFilterValue || this.search);
        },

        showEmptyState() {
            return !this.isFilterOrSearch && !this.loading && this.broadcasts.length === 0;
        },

        noFilterResults() {
            return !this.loading && Boolean(this.filter.value) && this.broadcasts.length === 0;
        },

        noSearchResults() {
            return !this.loading && Boolean(this.search) && this.broadcasts.length === 0;
        },

        shouldUpdateRecentlySent() {
            return this.lastSentEmailBroadcastId && this.firstLoad;
        },

        showOpensToggleIcon() {
            return this.showEmailOpens ? 'eye-hide' : 'eye-show';
        },

        showOpensToggleText() {
            return this.showEmailOpens ? this.$options.MESSAGE_CONSTANTS.HIDE_OPENS : this.$options.MESSAGE_CONSTANTS.SHOW_OPENS;
        },
    },

    methods: {
        openRescheduleModal(mailBatchId, broadcastSubject) {
            this.broadcastSubject = broadcastSubject;
            this.isRescheduleModalOpen = true;
            this.selectedMailBatchId = mailBatchId;
        },

        closeRescheduleModal() {
            this.isRescheduleModalOpen = false;
        },

        createNewEmailBroadcast() {
            this.$emit('new-unlayer-email');
        },

        getBroadcastFromBroadcastId(broadcastId) {
            return this.broadcasts.find(({ id }) => id === broadcastId);
        },

        getBroadcastFromBatchId(batchId) {
            return this.broadcasts.find(({ mailBatchId }) => mailBatchId === batchId);
        },

        async loadingFirstPageBroadcast() {
            const payload = {
                offset: this.offset,
                limit: this.limit,
                search: this.search,
                filter: this.filter?.value ?? null,
            };

            try {
                const { data } = await getAllEmailBroadcasts(payload);

                return data.emailBroadcastReportDTOList.length;
            } catch (error) {
                this.$error({ message: 'Error loading email broadcasts' });

                return 0;
            }
        },

        async loadEmailBroadcasts() {
            this.loading = true;

            const payload = {
                offset: this.offset,
                limit: this.limit,
                search: this.search,
                filter: this.filter?.value ?? null,
            };

            try {
                const { data } = await getAllEmailBroadcasts(payload);


                const parsedBroadcastList = data.emailBroadcastReportDTOList
                    .map(this.parseBroadcastReport)
                    .filter(Boolean); // Removes the null return values

                const responseLength = data.emailBroadcastReportDTOList.length;

                this.offset += this.limit; // should not be based on response length. Should be based on client side page limit
                this.broadcasts = [...this.broadcasts, ...parsedBroadcastList];
                this.noMoreData = responseLength === 0;

                if (this.firstLoad && !this.noMoreData) {
                    const noMoreDataSecondCall = await this.loadingFirstPageBroadcast();

                    this.noMoreData = noMoreDataSecondCall === 0;
                }

                if (this.shouldUpdateRecentlySent) {
                    const recentBroadcast = this.getBroadcastFromBroadcastId(this.lastSentEmailBroadcastId);
                    const isSendNow = !recentBroadcast.scheduleDate;

                    this.updateIndividualBroadcast({
                        ...recentBroadcast,
                        status: isSendNow ? BROADCAST_STATUS.PROCESSING : BROADCAST_STATUS.SCHEDULING,
                    });

                    await this.pollBroadcastByBroadcastId(this.lastSentEmailBroadcastId);
                }
            } catch (error) {
                this.$error({ message: 'Error loading email broadcasts' });
                this.noMoreData = true;
            } finally {
                this.loading = false;
                this.firstLoad = false;
            }
        },

        parseBroadcastReport({ broadcastReport }) {
            return {
                id: broadcastReport.broadcastId,
                subject: broadcastReport.subject,
                status: broadcastReport.status,
                splitBroadcastType: broadcastReport.splitBroadcastType,
                scheduleDate: broadcastReport.scheduleDate,
                lastEdit: broadcastReport.lastUpdated, // lastUpdated -> lastEdit
                endDate: broadcastReport.endDate,
                sentCount: broadcastReport.numSent, // numSent -> sentCount
                openCount: broadcastReport.numOpened, // numOpened -> openCount
                openPercent: broadcastReport.percentOpened, // percentOpened -> openPercent
                clickCount: broadcastReport.numClicked, // numClicked -> clickCount
                clickPercent: broadcastReport.percentClicked, // percentClicked -> clickPercent
                mailBatchId: broadcastReport.mailBatchId,
                editor: broadcastReport.editor,
            };
        },

        async pollBroadcastByBroadcastId(broadcastId, attempts = null) {
            if (attempts >= POLLING.MAX_ATTEMPTS) return;

            if (attempts === null) {
                clearTimeout(this.pollTimeout);
                this.pollTimeout = setTimeout(() => this.pollBroadcastByBroadcastId(broadcastId, 0), POLLING.INTERVAL);

                return;
            }

            try {
                const broadcastReport = await getUnlayerBroadcastReportByBroadcastId(broadcastId);
                const recentlySentBroadcast = this.parseBroadcastReport(broadcastReport);
                const { status, mailBatchId, scheduleDate } = recentlySentBroadcast;
                const isSendNow = !scheduleDate;
                const shouldUpdateStatus = (!isSendNow && VALID_DISPLAY_STATUSES.SCHEDULE.includes(status))
                        || (isSendNow && VALID_DISPLAY_STATUSES.SEND_NOW.includes(status));

                if (shouldUpdateStatus) this.updateIndividualBroadcast(recentlySentBroadcast);

                if (!mailBatchId || PENDING_STATUSES.includes(status)) {
                    clearTimeout(this.pollTimeout);
                    this.pollTimeout = setTimeout(() => this.pollBroadcastByBroadcastId(broadcastId, attempts + 1), POLLING.INTERVAL);
                } else if (status === BROADCAST_STATUS.QUEUED) {
                    const diff = isSendNow
                        ? 0
                        : moment(scheduleDate).diff(moment(), 'milliseconds');
                    const delay = Math.min(Math.max(diff, 0), POLLING.MAX_DELAY);

                    clearTimeout(this.pollTimeout);
                    this.pollTimeout = setTimeout(() => this.pollBroadcastByBroadcastId(broadcastId, attempts + 1), POLLING.INTERVAL + delay);
                }
            } catch (e) {
                this.updateIndividualBroadcast({
                    ...this.getBroadcastFromBroadcastId(this.lastSentEmailBroadcastId),
                    status: BROADCAST_STATUS.ERROR,
                });
                this.$error({ message: 'Error loading broadcast. Refresh the page' });
            }
        },

        updateIndividualBroadcast(broadcast) {
            const index = this.broadcasts.findIndex((b) => b.id === broadcast.id);

            if (index === -1) {
                return;
            }

            const updated = {
                ...this.broadcasts[index],
                ...broadcast,
            };

            this.broadcasts.splice(index, 1, updated);
        },

        stopBroadcast(mailBatchId) {
            stopEmailBroadcast(mailBatchId)
                .then(() => {
                    this.reload();
                    this.$toast({ message: 'Your broadcast has been stopped' });
                }).catch(() => {
                    this.$error({ message: 'Error cancelling email broadcast' });
                    this.noMoreData = true;
                    this.loading = false;
                });
        },

        resumeBroadcast(mailBatchId) {
            resumeEmailBroadcast(mailBatchId)
                .then(() => {
                    this.reload();
                    this.$toast({ message: 'Your broadcast has been resumed' });
                }).catch(() => {
                    this.$error({ message: 'Error resuming email broadcast sending' });
                    this.noMoreData = true;
                    this.loading = false;
                });
        },

        deleteDraft(editor, broadcastId) {
            const promise = editor === EMAIL_EDITORS.UNLAYER ? deleteUnlayerDraft(broadcastId) : deleteDraft(broadcastId);

            promise
                .then(() => {
                    this.reload();
                }).catch(() => {
                    this.$error({ message: 'Error deleting draft broadcast' });
                    this.noMoreData = true;
                    this.loading = false;
                });
        },

        editBroadcast({ editor, id }) {
            if (editor === EMAIL_EDITORS.UNLAYER) {
                this.$emit('edit-unlayer-broadcast', id);
            } else {
                // BardEmailBuilder
                window.location = `/app/email/broadcast/${id}`;
            }
        },

        async copyBroadcast({ editor, id }) {
            let newBroadcastId;

            try {
                if (editor === EMAIL_EDITORS.UNLAYER) {
                    this.showSpinnerContainer();

                    try {
                        newBroadcastId = await copyUnlayerBroadcast(id);
                    } catch (e) {
                        throw new Error(e);
                    } finally {
                        this.hideSpinnerContainer();
                    }
                } else {
                    const listId = 0;
                    const isSplitTest = false;
                    const params = {
                        broadcastId: id,
                        listId,
                        isSplitTest,
                    };

                    const resp = await copyBroadcast(params);

                    newBroadcastId = resp.data;
                }

                this.editBroadcast({ id: newBroadcastId, editor });
            } catch (e) {
                this.$error({ message: 'Error copying draft broadcast' });
            }
        },

        showSpinnerContainer() {
            this.scrollY = window.scrollY;

            const { top } = this.$root.$el.getBoundingClientRect();

            this.spinnerContainerPositionFixed = top <= 0;

            document.body.style.overflow = 'hidden';

            this.broadcastCopyInProgress = true;

            this.$nextTick(() => {
                document.body.scrollTo({
                    left: 0,
                    top: this.scrollY,
                    behavior: 'instant',
                });
            });
        },

        hideSpinnerContainer() {
            document.body.style.overflow = '';

            this.broadcastCopyInProgress = false;

            this.$nextTick(() => {
                document.body.scrollTo({
                    left: 0,
                    top: this.scrollY,
                    behavior: 'instant',
                });
            });
        },

        closeModalAndReload() {
            this.closeRescheduleModal();
            this.reload();
        },

        async rescheduleEmailBroadcast(time, selectedTimeZone) {
            this.$toast({ message: 'Your broadcast will be rescheduled shortly.' });
            this.updateIndividualBroadcast({
                ...this.getBroadcastFromBatchId(this.selectedMailBatchId),
                scheduleDate: time,
                status: BROADCAST_STATUS.RESCHEDULING,
            });

            try {
                const { data } = await rescheduleBroadcast(this.selectedMailBatchId, time, selectedTimeZone);

                if (!data) {
                    this.$error({ message: 'Error rescheduling broadcast' });
                }

                amplitude.keap.logEvent(amplitude.keap.events.EMAIL_BROADCAST_RESCHEDULED, {
                    'Event Source': 'Rescheduled from Menu',
                });
            } catch (e) {
                this.$error({ message: 'Error rescheduling broadcast' });
            } finally {
                this.closeRescheduleModal();
            }

            const broadcastId = this.getBroadcastFromBatchId(this.selectedMailBatchId).id;

            await this.pollBroadcastByBroadcastId(broadcastId);
        },

        reload() {
            this.offset = 0;
            this.broadcasts = [];
            this.firstLoad = true;
            this.loadEmailBroadcasts();
        },

        updateSearch() {
            this.reload();
        },

        completeApplePrivacyGuide() {
            this.showBroadcastsPageApplePrivacyGuide = false;
            completeApplePrivacyGuideEmailBroadcastPage();
        },

        openAppleMailPrivacyInNewTab() {
            window.open(OPEN_RATES_HELP_ARTICLE_URL);
        },

        toggleShowHideEmailOpens() {
            this.showEmailOpens = !this.showEmailOpens;
            saveShowHideOpenRatesUserPreference(this.showEmailOpens);
        },
    },
};
</script>

<style>
.email-broadcast-page .dropdown-menu-list {
    margin: 0;
}

.email-broadcast-page #search-entry > input{
    height: 24px;
}
</style>

<style lang="scss" scoped>
.email-broadcast-page {
    display: block;
}

.email-broadcast-page .filter #search-entry {
    width: 350px;
    margin-bottom: 0;
}

.email-broadcast-page .filter {
    display: flex;
    justify-content: space-between;
    padding-bottom: .75rem;
}
.email-broadcast-page .filter-actions {
    display: flex;
    align-items: center;
}

.email-broadcast-page .open-rates-guide {
    line-height: 1.5rem;
    display: flex;
    flex-direction: column;
}

.email-broadcast-page .opens-tooltip {
    --tooltip-width: #{px-to-rem(324)};
}

.email-broadcast-page .tooltip-body {
    text-align: left;
    line-height: 1.5rem;
}

.spinner-container {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 900;
    width: 100%;
    height: 100vh;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    opacity: 0.8;
    background-color: $color-paper;
}

.spinner-container.fixed {
    position: fixed;
}

.spinner-container:not(.fixed) > *  {
    position: relative;
    top: px-to-rem(-60px);
}

</style>
